/**
 * TalentPool Project enums — numeric (byte) values for request/response JSON.
 * Labels kept for UI display; normalize helpers accept number | string for safe compares.
 */

export enum ProjectType {
  FixedPrice = 1,
  HourlyRate = 2,
}

export enum ProjectStatus {
  Draft = 1,
  Pending = 2,
  Ongoing = 3,
  Completed = 4,
  Canceled = 5,
  Rejected = 6,
}

export enum MilestoneType {
  Multiple = 1,
  Single = 2,
}

export enum MilestoneStatus {
  NotStarted = 1,
  Requested = 2,
  InProgress = 3,
  Submitted = 4,
  PaymentProcessing = 5,
  Completed = 6,
  Canceled = 7,
  Rejected = 8,
}

export enum MilestoneAssignmentStatus {
  Pending = 1,
  Requested = 2,
  Accepted = 3,
  Submitted = 4,
  PaymentProceed = 5,
  Rejected = 6,
  Completed = 7,
}

export enum AssignmentEntityType {
  Consultant = 1,
  Agency = 2,
}

export enum ProjectMediaEntity {
  Project = 1,
  Milestone = 2,
  MilestoneAssignment = 3,
}

export enum ProjectHistory {
  Created = 1,
  Assigned = 2,
  Accepted = 3,
  Submitted = 4,
  Completed = 5,
  Canceled = 6,
  Requested = 7,
  PaymentProceed = 8,
  PaymentRecieved = 9,
  Rejected = 10,
  Approved = 11,
  InProgress = 12,
  DisputeRaised = 13,
  DisputeSettlementCompleted = 14,
  AutoApproved = 15,
}

const PROJECT_HISTORY_LABELS: Record<number, string> = {
  [ProjectHistory.Created]: 'Created',
  [ProjectHistory.Assigned]: 'Assigned',
  [ProjectHistory.Accepted]: 'Accepted',
  [ProjectHistory.Submitted]: 'Submitted',
  [ProjectHistory.Completed]: 'Completed',
  [ProjectHistory.Canceled]: 'Cancelled',
  [ProjectHistory.Requested]: 'Requested',
  [ProjectHistory.PaymentProceed]: 'Payment Proceed',
  [ProjectHistory.PaymentRecieved]: 'Payment Received',
  [ProjectHistory.Rejected]: 'Rejected',
  [ProjectHistory.Approved]: 'Approved',
  [ProjectHistory.InProgress]: 'In Progress',
  [ProjectHistory.DisputeRaised]: 'Dispute Raised',
  [ProjectHistory.DisputeSettlementCompleted]: 'Dispute Settlement Completed',
  [ProjectHistory.AutoApproved]: 'Auto Approved',
};

const PROJECT_HISTORY_FROM_KEY: Record<string, ProjectHistory> = {
  created: ProjectHistory.Created,
  assigned: ProjectHistory.Assigned,
  accepted: ProjectHistory.Accepted,
  submitted: ProjectHistory.Submitted,
  completed: ProjectHistory.Completed,
  canceled: ProjectHistory.Canceled,
  cancelled: ProjectHistory.Canceled,
  requested: ProjectHistory.Requested,
  startrequested: ProjectHistory.Requested,
  paymentproceed: ProjectHistory.PaymentProceed,
  paymentrecieved: ProjectHistory.PaymentRecieved,
  paymentreceived: ProjectHistory.PaymentRecieved,
  rejected: ProjectHistory.Rejected,
  approved: ProjectHistory.Approved,
  inprogress: ProjectHistory.InProgress,
  disputeraised: ProjectHistory.DisputeRaised,
  disputesettlementcompleted: ProjectHistory.DisputeSettlementCompleted,
  autoapproved: ProjectHistory.AutoApproved,
};

/**
 * Normalize string enum names for compare (e.g. "In-Progress" → "inprogress").
 * Numbers are ambiguous across ProjectStatus vs MilestoneStatus — use typed parsers instead.
 */
export const normalizeProjectEnumKey = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return '';
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[-_\s]/g, '');
};

const PROJECT_STATUS_LABELS: Record<number, string> = {
  [ProjectStatus.Draft]: 'Draft',
  [ProjectStatus.Pending]: 'Pending',
  [ProjectStatus.Ongoing]: 'Ongoing',
  [ProjectStatus.Completed]: 'Completed',
  [ProjectStatus.Canceled]: 'Cancelled',
  [ProjectStatus.Rejected]: 'Rejected',
};

const PROJECT_TYPE_LABELS: Record<number, string> = {
  [ProjectType.FixedPrice]: 'Fixed Price',
  [ProjectType.HourlyRate]: 'Hourly Rate',
};

const MILESTONE_STATUS_LABELS: Record<number, string> = {
  [MilestoneStatus.NotStarted]: 'Not Started',
  [MilestoneStatus.Requested]: 'Requested',
  [MilestoneStatus.InProgress]: 'In Progress',
  [MilestoneStatus.Submitted]: 'Submitted',
  [MilestoneStatus.PaymentProcessing]: 'Payment Processing',
  [MilestoneStatus.Completed]: 'Completed',
  [MilestoneStatus.Canceled]: 'Cancelled',
  [MilestoneStatus.Rejected]: 'Rejected',
};

const PROJECT_STATUS_FROM_KEY: Record<string, ProjectStatus> = {
  draft: ProjectStatus.Draft,
  pending: ProjectStatus.Pending,
  ongoing: ProjectStatus.Ongoing,
  completed: ProjectStatus.Completed,
  canceled: ProjectStatus.Canceled,
  cancelled: ProjectStatus.Canceled,
  rejected: ProjectStatus.Rejected,
};

const MILESTONE_STATUS_FROM_KEY: Record<string, MilestoneStatus> = {
  notstarted: MilestoneStatus.NotStarted,
  requested: MilestoneStatus.Requested,
  inprogress: MilestoneStatus.InProgress,
  submitted: MilestoneStatus.Submitted,
  paymentprocessing: MilestoneStatus.PaymentProcessing,
  completed: MilestoneStatus.Completed,
  canceled: MilestoneStatus.Canceled,
  cancelled: MilestoneStatus.Canceled,
  rejected: MilestoneStatus.Rejected,
};

const PROJECT_TYPE_FROM_KEY: Record<string, ProjectType> = {
  fixedprice: ProjectType.FixedPrice,
  fixed: ProjectType.FixedPrice,
  hourlyrate: ProjectType.HourlyRate,
  hourly: ProjectType.HourlyRate,
};

const MILESTONE_TYPE_FROM_KEY: Record<string, MilestoneType> = {
  multiple: MilestoneType.Multiple,
  multi: MilestoneType.Multiple,
  single: MilestoneType.Single,
};

const ASSIGNMENT_STATUS_FROM_KEY: Record<string, MilestoneAssignmentStatus> = {
  pending: MilestoneAssignmentStatus.Pending,
  requested: MilestoneAssignmentStatus.Requested,
  accepted: MilestoneAssignmentStatus.Accepted,
  submitted: MilestoneAssignmentStatus.Submitted,
  paymentproceed: MilestoneAssignmentStatus.PaymentProceed,
  rejected: MilestoneAssignmentStatus.Rejected,
  completed: MilestoneAssignmentStatus.Completed,
};

export const parseProjectStatus = (value: unknown): ProjectStatus | null => {
  if (typeof value === 'number' && value in PROJECT_STATUS_LABELS) {
    return value as ProjectStatus;
  }
  const key = normalizeProjectEnumKey(value);
  return PROJECT_STATUS_FROM_KEY[key] ?? null;
};

export const parseMilestoneStatus = (value: unknown): MilestoneStatus | null => {
  if (typeof value === 'number' && value in MILESTONE_STATUS_LABELS) {
    return value as MilestoneStatus;
  }
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    const asNum = Number(value.trim());
    if (asNum in MILESTONE_STATUS_LABELS) {
      return asNum as MilestoneStatus;
    }
  }
  const key = normalizeProjectEnumKey(value);
  return MILESTONE_STATUS_FROM_KEY[key] ?? null;
};

export const parseProjectType = (value: unknown): ProjectType | null => {
  if (typeof value === 'number' && value in PROJECT_TYPE_LABELS) {
    return value as ProjectType;
  }
  const key = normalizeProjectEnumKey(value);
  return PROJECT_TYPE_FROM_KEY[key] ?? null;
};

export const parseMilestoneType = (value: unknown): MilestoneType | null => {
  if (typeof value === 'number' && (value === MilestoneType.Multiple || value === MilestoneType.Single)) {
    return value as MilestoneType;
  }
  // API may send numeric enums as strings ("1" / "2")
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    const asNum = Number(value.trim());
    if (asNum === MilestoneType.Multiple || asNum === MilestoneType.Single) {
      return asNum as MilestoneType;
    }
  }
  const key = normalizeProjectEnumKey(value);
  return MILESTONE_TYPE_FROM_KEY[key] ?? null;
};

export const parseAssignmentStatus = (value: unknown): MilestoneAssignmentStatus | null => {
  if (typeof value === 'number' && value >= 1 && value <= 7) {
    return value as MilestoneAssignmentStatus;
  }
  const key = normalizeProjectEnumKey(value);
  return ASSIGNMENT_STATUS_FROM_KEY[key] ?? null;
};

export const parseProjectHistory = (value: unknown): ProjectHistory | null => {
  if (typeof value === 'number' && value in PROJECT_HISTORY_LABELS) {
    return value as ProjectHistory;
  }
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    const asNum = Number(value.trim());
    if (asNum in PROJECT_HISTORY_LABELS) {
      return asNum as ProjectHistory;
    }
  }
  const key = normalizeProjectEnumKey(value);
  return PROJECT_HISTORY_FROM_KEY[key] ?? null;
};

export const getProjectHistoryLabel = (value: unknown): string => {
  const parsed = parseProjectHistory(value);
  if (parsed != null) return PROJECT_HISTORY_LABELS[parsed];
  return '';
};

export const getProjectStatusLabel = (value: unknown): string => {
  const parsed = parseProjectStatus(value);
  if (parsed != null) return PROJECT_STATUS_LABELS[parsed];
  if (typeof value === 'string' && value.trim()) {
    const key = normalizeProjectEnumKey(value);
    if (key === 'canceled' || key === 'cancelled') return 'Cancelled';
    return value;
  }
  return '';
};

export const getProjectTypeLabel = (value: unknown): string => {
  const parsed = parseProjectType(value);
  if (parsed != null) return PROJECT_TYPE_LABELS[parsed];
  if (typeof value === 'string' && value.trim()) return value;
  return '';
};

export const getMilestoneStatusLabel = (value: unknown): string => {
  const parsed = parseMilestoneStatus(value);
  if (parsed != null) return MILESTONE_STATUS_LABELS[parsed];
  if (typeof value === 'string' && value.trim()) {
    const key = normalizeProjectEnumKey(value);
    if (key === 'canceled' || key === 'cancelled') return 'Cancelled';
    return value;
  }
  return '';
};

export const isProjectStatus = (value: unknown, expected: ProjectStatus): boolean =>
  parseProjectStatus(value) === expected;

export const isMilestoneStatus = (value: unknown, expected: MilestoneStatus): boolean =>
  parseMilestoneStatus(value) === expected;

export const isMilestoneType = (value: unknown, expected: MilestoneType): boolean =>
  parseMilestoneType(value) === expected;

export const isAssignmentStatus = (value: unknown, expected: MilestoneAssignmentStatus): boolean =>
  parseAssignmentStatus(value) === expected;

/** UI slug used across milestone cards: in-progress | requested | not-started | completed | cancelled */
export const mapMilestoneStatusToUi = (
  value: unknown
): 'in-progress' | 'requested' | 'not-started' | 'completed' | 'cancelled' => {
  const status = parseMilestoneStatus(value);
  switch (status) {
    case MilestoneStatus.InProgress:
    case MilestoneStatus.Submitted:
    case MilestoneStatus.PaymentProcessing:
      return 'in-progress';
    case MilestoneStatus.Completed:
      return 'completed';
    case MilestoneStatus.Canceled:
    case MilestoneStatus.Rejected:
      return 'cancelled';
    case MilestoneStatus.Requested:
      return 'requested';
    case MilestoneStatus.NotStarted:
    default:
      return 'not-started';
  }
};

export const isMilestoneNotStarted = (value: unknown): boolean =>
  isMilestoneStatus(value, MilestoneStatus.NotStarted);

export const isMilestoneRequested = (value: unknown): boolean =>
  isMilestoneStatus(value, MilestoneStatus.Requested);

export const isMilestoneInProgress = (value: unknown): boolean =>
  isMilestoneStatus(value, MilestoneStatus.InProgress);

export const isMilestoneSubmitted = (value: unknown): boolean =>
  isMilestoneStatus(value, MilestoneStatus.Submitted);

export const isMilestonePaymentProcessing = (value: unknown): boolean =>
  isMilestoneStatus(value, MilestoneStatus.PaymentProcessing);

export const isProjectInactiveStatus = (value: unknown): boolean =>
  isProjectStatus(value, ProjectStatus.Canceled) || isProjectStatus(value, ProjectStatus.Rejected);

export const isProjectPendingStatus = (value: unknown): boolean =>
  isProjectStatus(value, ProjectStatus.Pending);

export const isProjectOngoingStatus = (value: unknown): boolean =>
  isProjectStatus(value, ProjectStatus.Ongoing);

/** Comparable key for milestone status (works for API numbers and legacy strings). */
export const getMilestoneStatusKey = (value: unknown): string => {
  const parsed = parseMilestoneStatus(value);
  switch (parsed) {
    case MilestoneStatus.NotStarted:
      return 'notstarted';
    case MilestoneStatus.Requested:
      return 'requested';
    case MilestoneStatus.InProgress:
      return 'inprogress';
    case MilestoneStatus.Submitted:
      return 'submitted';
    case MilestoneStatus.PaymentProcessing:
      return 'paymentprocessing';
    case MilestoneStatus.Completed:
      return 'completed';
    case MilestoneStatus.Canceled:
      return 'canceled';
    case MilestoneStatus.Rejected:
      return 'rejected';
    default:
      return normalizeProjectEnumKey(value);
  }
};

/** Comparable key for assignment status (works for API numbers and legacy strings). */
export const getAssignmentStatusKey = (value: unknown): string => {
  const parsed = parseAssignmentStatus(value);
  switch (parsed) {
    case MilestoneAssignmentStatus.Pending:
      return 'pending';
    case MilestoneAssignmentStatus.Requested:
      return 'requested';
    case MilestoneAssignmentStatus.Accepted:
      return 'accepted';
    case MilestoneAssignmentStatus.Submitted:
      return 'submitted';
    case MilestoneAssignmentStatus.PaymentProceed:
      return 'paymentproceed';
    case MilestoneAssignmentStatus.Rejected:
      return 'rejected';
    case MilestoneAssignmentStatus.Completed:
      return 'completed';
    default:
      return normalizeProjectEnumKey(value);
  }
};

/** Comparable key for project status (works for API numbers and legacy strings). */
export const getProjectStatusKey = (value: unknown): string => {
  const parsed = parseProjectStatus(value);
  switch (parsed) {
    case ProjectStatus.Draft:
      return 'draft';
    case ProjectStatus.Pending:
      return 'pending';
    case ProjectStatus.Ongoing:
      return 'ongoing';
    case ProjectStatus.Completed:
      return 'completed';
    case ProjectStatus.Canceled:
      return 'canceled';
    case ProjectStatus.Rejected:
      return 'rejected';
    default:
      return normalizeProjectEnumKey(value);
  }
};
